'use client'

import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Search } from 'lucide-react'
import type { ExamSearchEntry } from '@/data/exams/search-index'

const CommandPalette = dynamic(() =>
  import('./CommandPalette').then((mod) => mod.CommandPalette)
)

interface PaletteLauncherProps {
  entries: ExamSearchEntry[]
}

/**
 * Navbar search entry point. Desktop: a button that lazy-loads the command
 * palette (also bound to Cmd/Ctrl+K). Mobile: a plain link to /exams — the
 * palette is a keyboard-first desktop surface.
 */
export function PaletteLauncher({ entries }: PaletteLauncherProps) {
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((current) => !current)
      }
    }
    const handleOpenEvent = (): void => setOpen(true)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('ps:open-palette', handleOpenEvent)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('ps:open-palette', handleOpenEvent)
    }
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search exams"
        aria-haspopup="dialog"
        aria-keyshortcuts="Meta+K"
        className="border-border bg-bg text-hint hover:bg-surface hover:text-body hidden h-10 items-center gap-2 rounded-full border px-3 text-sm transition-colors lg:inline-flex"
      >
        <Search size={15} />
        <kbd className="hidden text-[11px] font-medium [@media(pointer:fine)]:inline">
          ⌘K
        </kbd>
      </button>
      <Link
        href="/exams"
        prefetch={false}
        aria-label="Search exams"
        className="text-body hover:bg-surface hover:text-ink flex h-11 w-11 items-center justify-center rounded-full transition-colors lg:hidden"
      >
        <Search size={19} />
      </Link>
      {open && (
        <CommandPalette entries={entries} onClose={() => setOpen(false)} />
      )}
    </>
  )
}
